import Head from "next/head"
import {NextPage, GetStaticProps} from "next";
import {MainLayout} from "../src/components/main-layout";
import {Container, Box, TextField, Grid, Zoom, Button, Fade, FormHelperText, Hidden} from "@mui/material";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import {useTranslation} from "next-i18next";

import Card from "@mui/material/Card";
import {Formik} from "formik";
import * as Yup from "yup";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import CardActions from "@mui/material/CardActions";
import React, {useState, useEffect} from "react";
import {LoadingButton} from "@mui/lab";
import HappyIcon from "mdi-material-ui/EmoticonHappyOutline";
import {wait} from "../src/utils/wait";
import join from "lodash/join";
import {useAuth} from "../src/hooks/use-auth";
import Send from "@mui/icons-material/Send"
import { WhatsApp } from "../src/components/whatsapp";


/*

TODO: force phone number

when isabelle gets email, she can click a button that sends that user a template whatsapp message with maybe a link
to the trip that says "your message/inquiry has been read and we are busy responding"

 */

const ContactPage: NextPage = (props: any) => {

    const {i18n, t} = useTranslation();

    const {user} = useAuth()

    const [messageSent, setMessageSent] = useState(false);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [showThanks, setShowThanks] = useState(false);

    useEffect(() => {
        setPageLoaded(true);
    }, []);

    useEffect(() => {
        if (messageSent) {
            setTimeout(() => {
                setShowThanks(true);
            }, 1500);
        }
    }, [messageSent]);

    return (
        <div>
            <Head>
                <title>{t("title-contact") + " | SeaCrush"}</title>
            </Head>
            <section>
                <Container maxWidth='md' sx={{py: 4}}>

                    <Formik
                        initialValues={
                            {
                                name: join([user?.firstName, user?.lastName], " "),
                                email: user?.email ?? "",
                                phone: "",
                                message: "",
                                submit: null
                            }
                        }
                        validationSchema={
                            Yup
                                .object()
                                .shape({
                                    name: Yup
                                        .string()
                                        .min(1)
                                        .max(255)
                                        .required(t("validation.required")),
                                    email: Yup
                                        .string()
                                        .email(t("validation.email"))
                                        .max(255)
                                        .required(t("validation.required")),
                                    message: Yup
                                        .string()
                                        .min(10)
                                        .required(t("validation.required")),
                                })
                        }
                        onSubmit={async (values, {
                            resetForm,
                            setErrors,
                            setStatus,
                            setSubmitting
                        }): Promise<void> => {

                            setSubmitting(true);

                            try {

                                const response = await fetch("/api/contact/submission", {
                                    body: JSON.stringify({
                                        name: values.name,
                                        email: values.email,
                                        telephone: values.phone,
                                        country: "HARDCODED",
                                        message: values.message,
                                        siteLanguage: i18n.language
                                    }),
                                    headers: {
                                        "Content-Type": "application/json"
                                    },
                                    method: "post"
                                })


                                if (response.status === 200) {
                                    await wait(500);
                                    resetForm();
                                    setStatus({success: true});
                                    setSubmitting(false);
                                    setMessageSent(true)
                                    setShowThanks(true)
                                    //toast.success('Message sent!');
                                } else {
                                    throw Error("Failure to send message! Try dropping an email to hello@seacrush.com")
                                }


                                // NOTE: Make API request

                            } catch (err) {
                                console.error(err);
                                //toast.error('Something went wrong!');
                                setStatus({success: false});
                                // @ts-ignore
                                setErrors({submit: err.message});
                                setSubmitting(false);
                            }
                        }}
                    >
                        {({
                              errors,
                              handleBlur,
                              handleChange,
                              handleSubmit,
                              isSubmitting,
                              touched,
                              values
                          }): JSX.Element => (
                            <form onSubmit={handleSubmit}>

                                <Zoom in={showThanks} mountOnEnter={true} unmountOnExit={true}>
                                    <Card style={{
                                        maxWidth: "700px",
                                        margin: "0px auto",
                                        paddingBottom: 24,
                                        marginTop: 48
                                    }}>
                                        <CardContent>
                                            <Typography variant='h6' id='modal-title' align={"center"}>
                                                {t("thank-you")}
                                            </Typography>
                                            <Typography
                                                variant='subtitle1'
                                                id='simple-modal-description'
                                                align={"center"}
                                            >
                                                {t("be-in-touch")}{" "}
                                                <HappyIcon
                                                    style={{verticalAlign: "bottom", width: 32, height: 32}}
                                                />
                                            </Typography>
                                        </CardContent>
                                        <br/>
                                        <CardActions>
                                            <Grid
                                                container
                                                alignItems={"center"}
                                                justifyContent={"space-evenly"}
                                                spacing={2}
                                            >
                                                <Grid item>
                                                    <Button
                                                        onClick={(e) => {
                                                            setShowThanks(false);
                                                            setMessageSent(false);
                                                        }}
                                                        variant={"outlined"}
                                                        color='primary'
                                                    >
                                                        {t("send-another-message")}
                                                    </Button>
                                                </Grid>


                                                <Grid item>
                                                    <Button
                                                        component={"a"}
                                                        href={"/"}
                                                        variant={"contained"}
                                                        color='primary'
                                                        style={{color: "white"}}
                                                    >
                                                        {t("find-more-trips")}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </CardActions>
                                    </Card>
                                </Zoom>

                                <Fade
                                    in={pageLoaded && !messageSent}
                                    timeout={{
                                        exit: 1,
                                        enter: 600,
                                        appear: 1200

                                    }}
                                    style={{
                                        transitionDelay: isSubmitting ? "10ms" : "500ms",
                                        height: isSubmitting || messageSent ? "30vh" : "inherit"
                                    }}
                                    unmountOnExit={false}
                                >
                                    <Box sx={{position: "relative", zIndex: 100}}>
                                        <Card elevation={5}>

                                            <CardContent sx={{position: "relative"}}>
                                                <Hidden smDown>
                                                    <Box sx={{position: "absolute", top: 16, right: 32}}>
                                                        <Typography paragraph={true} style={{display: "inline"}}>
                                                            {t("we-respond-quickly")}
                                                        </Typography>
                                                        <img
                                                            style={{
                                                                width: 61,
                                                                height: 55,
                                                                display: "inline-block",
                                                                marginLeft: 24,
                                                                verticalAlign: "middle"
                                                            }}
                                                            src='/images/seacrush_heart.svg'
                                                        />
                                                    </Box>
                                                </Hidden>

                                                <TextField
                                                    style={{marginTop: 34, paddingTop: 8}}
                                                    name='message'
                                                    id='message'
                                                    placeholder={t("tell-us-more")}
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                    label={
                                                        <Typography
                                                            style={{fontWeight: "bold", paddingBottom: 16}}
                                                            variant='h5'
                                                        >
                                                            {t("dear")} SeaCrush,
                                                        </Typography>
                                                    }
                                                    multiline
                                                    maxRows='5'
                                                    minRows='3'
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    value={values.message}
                                                    error={Boolean(touched.message && errors.message)}
                                                    helperText={touched.message && errors.message}
                                                    margin='normal'
                                                    fullWidth
                                                    variant={"standard"}
                                                />

                                                <Typography fontWeight={"bold"} sx={{my: 2}} variant='h5'
                                                            component={"div"}>
                                                    {t("sincerely")},
                                                </Typography>

                                                <Grid
                                                    container
                                                    spacing={3}
                                                    alignItems='flex-start'
                                                    justifyContent='space-between'
                                                >
                                                    <Grid item xs={12} md={4}>
                                                        <TextField
                                                            label={t("name")}
                                                            name='name'
                                                            required
                                                            fullWidth
                                                            onBlur={handleBlur}
                                                            onChange={handleChange}
                                                            value={values.name}
                                                            error={Boolean(touched.name && errors.name)}
                                                            helperText={touched.name && errors.name}
                                                            variant={"standard"}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={12} md={4}>
                                                        <TextField
                                                            label='Email'
                                                            name='email'
                                                            required
                                                            onBlur={handleBlur}
                                                            onChange={handleChange}
                                                            fullWidth
                                                            type='email'
                                                            value={values.email}
                                                            error={Boolean(touched.email && errors.email)}
                                                            helperText={touched.email && errors.email}
                                                            variant={"standard"}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <TextField
                                                            label={t("telephone")}
                                                            name='phone'
                                                            autoComplete='tel'
                                                            onBlur={handleBlur}
                                                            onChange={handleChange}
                                                            fullWidth
                                                            type='tel'
                                                            value={values.phone}
                                                            error={Boolean(touched.phone && errors.phone)}
                                                            helperText={touched.phone && errors.phone}
                                                            variant={"standard"}
                                                        />
                                                        <FormHelperText id='telephone'>
                                                            {t("if-like-us-to-call")}
                                                        </FormHelperText>

                                                    </Grid>
                                                </Grid>

                                                {errors.submit && (
                                                    <FormHelperText error>
                                                        {errors.submit}
                                                    </FormHelperText>
                                                )}
                                            </CardContent>
                                            <CardActions style={{justifyContent: "flex-end"}}>
                                                <LoadingButton
                                                    type='submit'
                                                    size='medium'
                                                    color='secondary'
                                                    variant={"contained"}
                                                    disabled={isSubmitting}
                                                    loading={isSubmitting}
                                                    endIcon={<Send
                                                        sx={{paddingBottom: "2px"}}
                                                    />}

                                                >
                                                    {t("send-message")}
                                                </LoadingButton>


                                            </CardActions>

                                        </Card>
                                    </Box>
                                </Fade>
                            </form>)
                        }
                    </Formik>
                </Container>
            </section>
        </div>
    )
}


ContactPage.getLayout = (page) => (
    <MainLayout>
        {page}
        <WhatsApp/>
    </MainLayout>
);


// export const getServerSideProps = async (context: NextPageContext) => {
//
//     const {locale} = context
//     const cookies = nookies.get(context);
//
//     //verify that the user accessing this page is logged in
//
//
//     let uid = "undefined"
//
//     try {
//         uid = cookies.token ? (await auth.verifyIdToken(cookies.token)).uid : "undefined"
//     } catch (e) {
//         console.log(e)
//     }
//
//     let userData
//
//     const userSnap = await firestore.doc(`/users/${uid}`).get()
//
//     if (userSnap.exists) {
//         const {firstName, lastName, email} = userSnap.data() as any
//
//         userData = {
//             firstName: firstName ?? "",
//             lastName: lastName ?? "",
//             email: email ?? "",
//         }
//     } else {
//         userData = {
//             firstName: "",
//             lastName: "",
//             email: "",
//         }
//     }
//
//
//     return {
//         props: {
//             userData,
//             ...(await serverSideTranslations(locale as string, ['frontend']))
//         }
//     };
//
// }


export const getStaticProps: GetStaticProps = async ({locale}) => ({
    props: {
        ...(await serverSideTranslations(locale as string, ["frontend"])),
    },
})


export default ContactPage
